import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

/**
 * What is LEFT of the Agenda hook after the tab was retired (post-Affinity S1, 2026-09-01).
 *
 * The Agenda tab, its planner, its config view and the whole presentation deck were deleted by
 * owner ruling — and with them every write hook here (plan / slot CRUD / exceptions / cadence /
 * action items) and the presentation payload. What survives is a single READ, because Intel — which
 * stays, frozen, by the same set of rulings — asks the agenda when a sector next comes up so the
 * chunk editor can default a chunk's `week_of` to that Monday
 * (`components/intel/nextMeeting.ts`). That one consumer is the whole reason
 * `GET /api/agenda/meetings` and `agenda_scheduler.load_meetings` survive the wave.
 *
 * ⛔ Do not re-add write hooks here. `agenda_slots` / `meeting_exceptions` /
 * `agenda_cadence_config` have no writer left in the app; the read returns the standing rows plus
 * the computed weekly touches for active sectors, which is what Intel's question needs.
 */

export interface AgendaSlot {
  id: number | null;
  meeting_date: string;
  position: number;
  slot_type:
    | 'active_touch'
    | 'active_deepdive'
    | 'monitoring'
    | 'emerging'
    | 'custom'
    | 'other_pipeline';
  sector_id: number | null;
  sector_name: string | null;
  sector_stage: string | null;
  custom_title: string | null;
  is_manual_override: boolean;
  notes: string | null;
  is_computed: boolean;
}

export interface AgendaException {
  id: number;
  original_monday: string;
  exception_type: 'blackout' | 'reschedule';
  alternate_meeting_date: string | null;
  cascade_behavior: 'cascade_all' | 'shift_future_forward' | 'no_cascade' | 'per_item' | null;
  per_item_map: Record<string, string> | null;
  reason: string | null;
}

export interface AgendaMeeting {
  meeting_date: string;
  is_exception: boolean;
  exception: AgendaException | null;
  slots: AgendaSlot[];
}

export function useAgendaMeetings(startMonday: string, weeks: number) {
  return useQuery<AgendaMeeting[]>({
    queryKey: ['agenda-meetings', startMonday, weeks],
    queryFn: () =>
      apiFetch<AgendaMeeting[]>(`/api/agenda/meetings?start=${startMonday}&weeks=${weeks}`),
    staleTime: 60 * 1000,
  });
}
